import React, { useMemo, Fragment, useState } from 'react'
import { get } from 'lodash'

import { ToshiOsAddPointsLog } from '@/features/toshi_os_add_points_logs/types'

import { formatDateTime, t } from '@/lib/helpers'

import {
  DataTable,
  TableResendButton,
} from '@/components/shared/Table'

import { useRetryRequest } from '@/features/toshi_os_add_points_logs/mutations'

type Props = {
  data: ToshiOsAddPointsLog[]
}

export const ToshiOsAddPointsLogTable = ({ data }: Props) => {
  const { mutateAsync: retryRequest } = useRetryRequest();
  const [retryingIds, setRetryingIds] = useState<Set<number>>(new Set());


  const handleRetry = (id: number) => {
    setRetryingIds((prev) => {
      if (prev.has(id)) return prev;

      const newSet = new Set(prev);
      newSet.add(id);

      retryRequest(id)
        .finally(() => {
          setRetryingIds((current) => {
            const updated = new Set(current);
            updated.delete(id);
            return updated;
          });
        });

      return newSet;
    });
  };

  const columns = useMemo(() => {
    return [
      {
        Header: t('views.admin.toshi_os_add_points_logs.index.user'),
        accessor: 'user_email',
        disableSortBy: true,
        width: '10%',
      },
      {
        Header: t('views.admin.toshi_os_add_points_logs.index.transaction_id'),
        accessor: 'transaction_id',
        disableSortBy: true,
        width: '10%',
      },
      {
        Header: t('views.admin.toshi_os_add_points_logs.index.points'),
        accessor: 'points',
        disableSortBy: true,
        width: '5%',
      },
      {
        Header: t('views.admin.toshi_os_add_points_logs.index.summary'),
        accessor: 'summary',
        disableSortBy: true,
        width: '10%',
      },
      {
        Header: t('views.admin.toshi_os_add_points_logs.index.url'),
        accessor: 'url',
        disableSortBy: true,
        width: '15%',
      },
      {
        Header: t('views.admin.toshi_os_add_points_logs.index.status_code'),
        accessor: 'status_code',
        disableSortBy: true,
        width: '5%',
      },
      {
        Header: t('views.admin.toshi_os_add_points_logs.index.response'),
        accessor: 'response.error',
        disableSortBy: true,
        width: '15%',
      },
      {
        Header: t('views.admin.toshi_os_add_points_logs.index.timestamp'),
        accessor: 'timestamp',
        disableSortBy: true,
        width: '10%',
        Cell: (cellProps) => {
          const value = get(cellProps, 'value')

          return formatDateTime(value, 'YYYY-MM-DD HH:mm')
        },
      },
      {
        Header: t('views.admin.toshi_os_add_points_logs.index.attempts'),
        accessor: 'attempts',
        disableSortBy: true,
        width: '5%',
      },
      {
        Header: t('views.admin.toshi_os_add_points_logs.index.retry_status'),
        accessor: 'retry_status',
        disableSortBy: true,
        width: '10%',
        Cell: (cellProps) => {
          const value = get(cellProps, 'value')

          return t(
            `views.admin.toshi_os_add_points_logs.retry_statuses.${value}`
          )
        },
      },
      {
        Header: t('views.admin.toshi_os_add_points_logs.index.action'),
        accessor: 'action',
        disableSortBy: true,
        width: '5%',
        Cell: (cellProps) => {
          const row = get(cellProps, 'row.original')
          const { id } = row

          if (row.retry_status !== 'waiting_for_retry' && row.retry_status !== 'manual_retrying') return null

          return (
            <TableResendButton
              ariaLabel="resend"
              onClick={() => handleRetry(id)}
              disabled={
                row.retry_status !== 'waiting_for_retry' ||
                retryingIds.has(id)
              }
            />
          )
        },
      },
    ]
    // retryingIds is read inside these columns, so memoising on [] froze the disabled
    // state at its first value and the button never greyed out.
  }, [retryingIds])

  const memoizedData = useMemo(() => {
    return data
  }, [data])

  return (
    <Fragment>
      <DataTable data={memoizedData} columns={columns} />
    </Fragment>
  )
}
